import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

import { Observable } from 'rxjs';

import { ENVIRONMENT } from '../providers/environment.provider';
import { SSRService } from './ssr.service';
import { TokenService } from './token.service';

export interface IAuthEnvironment {
  authUrl: string;
  siteId: string;
}

type TAuthResult = 'closed' | 'success' | 'error';

export interface IAuthResult {
  status: TAuthResult;
  error?: string;
  key?: string;
  keyApply?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly width = 500;
  private readonly height = 750;

  private left?: number;
  private top?: number;
  private readonly _env = inject(ENVIRONMENT);
  private readonly _tokenService = inject(TokenService);

  private readonly _renderer: Renderer2;

  private _listenerCleanups: Array<() => void> = [];

  constructor(
    private readonly _SSRService: SSRService,
    rendererFactory: RendererFactory2,
  ) {
    this._renderer = rendererFactory.createRenderer(null, null);
  }

  open(lang: string = 'en'): Observable<IAuthResult> {
    if (!this._SSRService.isBrowserOnly) {
      return new Observable<IAuthResult>((observer) => {
        observer.error('Not in browser environment');
      });
    }

    this.left = window.innerWidth / 2 - this.width / 2 + window.screenX;
    this.top = window.innerHeight / 2 - this.height / 2 + window.screenY;

    const popup = window.open(
      `${this._env.authUrl}#siteId=${this._env.siteId}&language=${lang}&returnUrl=${window.location.origin}${window.location.pathname}`,
      'authPopup',
      `width=${this.width},height=${this.height},left=${this.left},top=${this.top}`,
    );

    return new Observable<IAuthResult>((observer) => {
      if (!popup) {
        observer.error('Popup blocked');
        return;
      }

      const closeInterval = setInterval(() => {
        if (popup.closed) {
          observer.next({ status: 'closed' });
          observer.complete();
          this._cleanup();
          clearInterval(closeInterval);
        }
      }, 500);
      this._listenerCleanups = [
        this._renderer.listen(window, 'message', (event: MessageEvent<any>) => {
          if (event.origin !== this._env.authUrl) {
            return;
          }

          const { status, error, key } = event.data;
          observer.next({ status, error, key });

          if (status === 'success' || status === 'error') {
            observer.complete();
            this._cleanup();
            popup.close();
          }
        }),
        this._renderer.listen(window, 'focus', () => {
          if (!popup.closed) {
            popup.focus();
          }
        }),
        () => clearInterval(closeInterval),
      ];

      return () => {
        this._cleanup();
        if (!popup.closed) {
          popup.close();
        }
      };
    });
  }

  private _cleanup() {
    this._listenerCleanups.forEach((fn) => fn());
    this._listenerCleanups = [];
  }
}
